import { BlockDataAttributes } from '@unifyapps/carbon/no-code/hooks/useBlockDataAttributes';
import type { BlockControlMethodHelpers } from '@unifyapps/carbon/no-code/types/blockDefinition';
import { snapdomToImg } from '@unifyapps/carbon/utils/snapdomToImg';
import type { BlockStateUnionType } from '@unifyapps/defs/types/block';

/**
 * Generates a preview image of the block element using the `@zumer/snapdom` library.
 *
 * ---
 * ## Why we switched off `html2canvas`
 *
 * We were previously using `html2canvas` for preview generation, but it produced
 * visually incorrect output — specifically, data labels rendered by VictoryCharts
 * were shifted/misplaced in the captured image.
 *
 * ### Root Cause
 * VictoryCharts renders data labels as HTML elements embedded inside SVG
 * `<foreignObject>` tags. `html2canvas` works by walking the DOM and calculating
 * element positions using the HTML/CSS box model. However, elements inside
 * `<foreignObject>` exist in SVG coordinate space, which uses its own
 * transform/viewBox system. `html2canvas` does not reconcile these two coordinate
 * systems correctly, causing labels to render at wrong positions.
 *
 * ### Known `html2canvas` Issues (open/unresolved)
 * - Text downshift inside SVG:
 *   https://github.com/niklasvh/html2canvas/issues/2775
 *
 * - Font color inside `<foreignObject>` not captured correctly:
 *   https://github.com/niklasvh/html2canvas/issues/2799
 *
 * - Font rendering and opacity issues with `foreignObjectRendering`:
 *   https://github.com/niklasvh/html2canvas/issues/1295
 *
 * - Elements captured with incorrect positional offset:
 *   https://github.com/niklasvh/html2canvas/issues/2398
 *
 * ### Why serialize-to-`<foreignObject>` capture works
 * `html-to-image` (used here before snapdom) and `snapdom` both take a
 * fundamentally different approach — they serialize the DOM into an SVG
 * `<foreignObject>` data URI and let the browser's own rendering engine rasterize
 * it. This means the browser handles both the HTML/CSS box model and SVG
 * coordinate space natively, so `<foreignObject>`-based content (like
 * VictoryCharts labels) is captured exactly as it appears on screen.
 *
 * ### Why `snapdom` over `html-to-image`
 * `html-to-image` walks the tree applying computed styles node by node, which
 * blocks the main thread long enough to visibly freeze the UI on a chart-sized
 * subtree. That stall is especially bad here because this method sits on the
 * excel/csv export path — the export chain waits on the `preview` written below
 * before it can trigger. snapdom clones and inlines styles in a single pass.
 */
export async function generatePreview(
  blockState: BlockStateUnionType,
  helpers: BlockControlMethodHelpers,
) {
  try {
    const blockEl = document.querySelector<HTMLElement>(
      `[${BlockDataAttributes.BlockId}="${blockState.id}"]`,
    );

    if (!blockEl) {
      helpers.showSnackbar({
        title: 'Something went wrong while generating preview',
        color: 'error',
      });
      console.error('Error generating preview: ', 'Block element not found');
      return blockState;
    }

    const image = await snapdomToImg(blockEl);

    return {
      ...blockState,
      preview: image,
    };
  } catch (error) {
    helpers.showSnackbar({
      title: 'Something went wrong while generating preview',
      color: 'error',
    });
    console.error('Error generating preview', error);
    return blockState;
  }
}
